import { Tooltip } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import styled from 'styled-components';
import { Goal, GoalsApi } from 'api/goal';
import { ReactComponent as NoStatusIcon } from 'assets/svgs/no-status.svg';
import { Button, LinkButton } from 'components/atoms/button';
import { TaskCompleted as CompletedIcon } from 'components/atoms/icons';
import { ActionBar } from 'components/molecules/action-bar';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';

const TodoIcon = styled(NoStatusIcon)`
  color: var(--cool-gray-40);
`;

const StyledActionBar = styled(ActionBar)`
  background: var(--white-100);
  margin-bottom: var(--spacing-03);

  && .ant-btn {
    min-width: auto;
    width: auto;
  }
`;

export const CarouselActionBar: FC<{
  goal: Goal;
  projectId: number;
  onEdit(goal: Goal): void;
}> = ({ goal, projectId, onEdit }) => {
  const [goalStatus, setStatus] = useState(false);
  const markCompleteTooltip = useTranslation(
    'project.overview.business_goals.tooltip.mark_todo',
  );
  const markTodoTooltip = useTranslation(
    'project.overview.business_goals.tooltip.mark_completed',
  );

  const { mutate: onSave } = useMutation((status: Pick<Goal, 'status'>) => {
    return GoalsApi.saveGoal(projectId, {
      ...goal,
      ...status,
    });
  });

  useEffect(() => {
    setStatus(goal.status);
  }, [goal.status]);

  const updateGoalStatus = () => {
    const updatedGoalStatus = !goalStatus;

    setStatus(updatedGoalStatus);
    onSave(
      { status: updatedGoalStatus },
      {
        onError() {
          setStatus(goalStatus);
          toastService({
            title: 'Error',
            message: 'There was an error updating the goal status',
          }).error();
        },
      },
    );
  };

  return (
    <StyledActionBar>
      <ActionBar.Primary>
        <Tooltip title={goalStatus ? markCompleteTooltip : markTodoTooltip}>
          <Button
            type="text"
            icon={goalStatus ? <CompletedIcon /> : <TodoIcon />}
            onClick={updateGoalStatus}
          />
        </Tooltip>
      </ActionBar.Primary>
      <ActionBar.Secondary>
        <LinkButton onClick={() => onEdit(goal)}>
          <FormattedMessage id="edit" />
        </LinkButton>
      </ActionBar.Secondary>
    </StyledActionBar>
  );
};
